import { CUSTOMER_INTEREST_SUGGESTIONS, CUSTOMER_SIGN_UP_TAG } from '@mds/shared/types/customer';

import type { CommsPrefs, Customer } from '@/types';

/** The categories the programme recruits from. Mirrors CustomerType. */
export const CUSTOMER_TYPES = [
  'Member',
  'Student',
  'Graduate',
  'Freelancer',
  'Teacher',
  'Business',
  'Visitor',
  'Company',
] as const;

/**
 * Tags suggested in the editor. Free text is still allowed — these are the ones
 * the front desk reaches for often enough to be worth one click.
 */
export const TAG_POOL = [
  'Newsletter',
  'Workshop attendee',
  'Founding member',
  'Student rate',
  'Volunteer',
  'Speaker',
  'Partner',
  'Lapsed',
  CUSTOMER_SIGN_UP_TAG,
];

/** Interests suggested in the editor. Also free text. Shared with the public sign-up page. */
export const INTEREST_POOL: string[] = [...CUSTOMER_INTEREST_SUGGESTIONS];

/** Everyone opted in on paper unless the record says otherwise. */
const allChannels: CommsPrefs = { email: true, whatsapp: true, sms: false };

/** Mock customer directory. Ids are referenced by the booking fixtures. */
export const CUSTOMERS: Customer[] = [
  {
    id: 'CU-301',
    name: 'Yara Kfoury',
    email: 'yara.k@mail.com',
    phone: '03 118 220',
    type: 'Member',
    tags: ['Founding member', 'Newsletter'],
    interests: ['Coworking', 'Design'],
    comms: allChannels,
    notes: 'Books the same desk most mornings. Happy to be asked for a testimonial.',
    events: [
      { event: 'Open House & Space Tour', date: 'Thu 17 Sep', seats: 1, attended: true },
      { event: 'Founders Night at the Café', date: 'Sat 26 Sep', seats: 2, attended: false },
    ],
    joined: '2026-03-04',
    bookings: 24,
    balance: 0,
  },
  {
    id: 'CU-302',
    name: 'Karim Nasr',
    email: 'karim.nasr@mail.com',
    phone: '71 664 300',
    type: 'Student',
    tags: ['Student rate', 'Workshop attendee'],
    interests: ['Software', 'Startups'],
    comms: { email: true, whatsapp: true, sms: true },
    notes: 'Evening desk package: university timetable, rarely in before 6 PM.',
    events: [{ event: 'Digital Skills Workshop', date: 'Wed 7 Oct', seats: 1, attended: true }],
    joined: '2026-08-19',
    bookings: 3,
    balance: 0,
  },
  {
    id: 'CU-303',
    name: 'Beirut Digital Co.',
    email: 'ops@bdc.com',
    phone: '06 555 666',
    type: 'Company',
    tags: ['Partner', 'Newsletter'],
    interests: ['Coworking', 'Marketing'],
    comms: { email: true, whatsapp: false, sms: false },
    notes: 'Invoices monthly to the ops address. Ask for a PO number before the training room.',
    events: [{ event: 'Founders Night at the Café', date: 'Sat 26 Sep', seats: 3, attended: true }],
    joined: '2026-01-22',
    bookings: 11,
    balance: 0,
  },
  {
    id: 'CU-304',
    name: 'Sami Traboulsi',
    email: 'sami.t@mail.com',
    phone: '76 220 118',
    type: 'Freelancer',
    tags: ['Workshop attendee'],
    interests: ['Podcasting', 'Photography'],
    comms: allChannels,
    notes: 'Records in the studio on Fridays. Owns his own mics, only books the room.',
    events: [
      { event: 'Podcast Recording Clinic', date: 'Fri 16 Oct', seats: 1, attended: false },
      { event: 'Open House & Space Tour', date: 'Thu 17 Sep', seats: 2, attended: true },
    ],
    joined: '2026-06-02',
    bookings: 8,
    balance: 0,
  },
  {
    id: 'CU-305',
    name: 'Rima Osman',
    email: 'rima.osman@mail.com',
    phone: '70 445 019',
    type: 'Visitor',
    tags: ['Lapsed'],
    interests: ['Coworking'],
    comms: { email: false, whatsapp: true, sms: false },
    notes: 'Deposit taken for the café hall, balance due on the night.',
    events: [],
    joined: '2026-09-01',
    bookings: 1,
    balance: 140,
  },
  {
    id: 'CU-306',
    name: 'Nour Zeidan',
    email: 'nour.z@mail.com',
    phone: '78 909 121',
    type: 'Teacher',
    tags: ['Speaker', 'Newsletter'],
    interests: ['Teaching', 'Design'],
    comms: allChannels,
    notes: 'Ran a session in the training room in August. Willing to run another.',
    events: [{ event: 'Open House & Space Tour', date: 'Thu 17 Sep', seats: 1, attended: true }],
    joined: '2026-07-14',
    bookings: 2,
    balance: 0,
  },
];

/** Every tag in use, for the directory filter. */
export const TAGS_IN_USE = [...new Set(CUSTOMERS.flatMap((c) => c.tags))].sort();
